// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Home › Closed (FE-05, AB#34846): one newest-first list mixing every closed
// kind — alarms, anomalies, routine items and recommendations — each card led
// by a kind chip, with kind filter chips (counts computed here) and a search
// box over title / id / site.
//
// Sources: closed rows of the console set (alarms; routine / optwork rows once
// an upstream returns them), closed findings from the anomalies API (closed
// via "Not an alarm" → CLOSED), and decided plans from HITL. The kind of a row
// is its source: the console set never marks a row unvalidated, so findings
// are bucketed by coming from the anomalies query, not by `validated`.
//
// Order: close time, newest first — `closedAt` for alarms and findings, the
// decision time for plans. AEM is only now adding `closed_at`, so a row still
// without it sorts after every dated item (newest raise first among those);
// its card reads "Closed —" for the same reason.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionableNotification, Search } from '@carbon/react';
import { useConsoleAlarms } from '../../hooks/useConsoleAlarms';
import { useAnomalies } from '../../hooks/useAnomalies';
import { useSiteApprovals } from '../../hooks/useSiteApprovals';
import { usePersona } from '../../hooks/usePersona';
import { useAssetDomainOf } from '../../hooks/useAssetDomainOf';
import { useAlarmsViewStore } from '../../stores/alarmsViewStore';
import { alarmDetailPath, anomalyDetailPath } from '../../services/alarmSelectors';
import { createdAtMs, decidedPlans } from '../../services/planSelectors';
import { getStringAttr } from '../../utils/planAttributes';
import { StaleDataNotice } from '../common/StaleDataNotice';
import { EmptyState, Loading } from './ScreenHelpers';
import { AlarmCard } from './alarms/AlarmCard';
import { ClosedPlanCard } from './ClosedPlanCard';
import type { Alarm } from '../../types/alarm';
import type { HitlApproval } from '../../types';

export type ClosedKind = 'alarm' | 'anomaly' | 'routine' | 'recommendation';

/** Chip order and labels, per the FE-05 mock. */
export const CLOSED_KINDS: { key: ClosedKind; label: string }[] = [
  { key: 'alarm', label: 'Alarm' },
  { key: 'anomaly', label: 'Anomalies' },
  { key: 'routine', label: 'Routine' },
  { key: 'recommendation', label: 'Recommendation' },
];

const KIND_LABEL: Record<ClosedKind, string> = {
  alarm: 'Alarm',
  anomaly: 'Anomaly',
  routine: 'Routine',
  recommendation: 'Recommendation',
};

/**
 * Which closed bucket a row belongs to. A row from the anomalies query is an
 * anomaly whatever its fields say; a console row is bucketed by its kind.
 */
export function closedKindOf(a: Alarm, source: 'console' | 'anomalies'): ClosedKind {
  if (source === 'anomalies') return 'anomaly';
  if (a.kind === 'routine') return 'routine';
  if (a.kind === 'optwork') return 'recommendation';
  return 'alarm';
}

interface ClosedItem {
  key: string;
  kind: ClosedKind;
  /** Close instant (ms) for the newest-first order; 0 (unknown) sorts last. */
  closedAt: number;
  /** Tie-break among rows with no close time: raise instant (ms), newest first. */
  raisedAt: number;
  /** Lower-cased title / id / site, what the search box matches against. */
  haystack: string;
  node: React.ReactNode;
}

const ms = (iso: string | null | undefined) => createdAtMs(iso);
const hay = (...parts: (string | undefined | null)[]) =>
  parts.filter(Boolean).join(' ').toLowerCase();

export function ClosedQueue({ siteId, siteLabel }: { siteId?: string; siteLabel?: string }) {
  const navigate = useNavigate();
  const layout = useAlarmsViewStore(s => s.layout);
  const [kind, setKind] = useState<ClosedKind | 'all'>('all');
  const [query, setQuery] = useState('');

  const alarmsQuery = useConsoleAlarms(siteId);
  const anomaliesQuery = useAnomalies(siteId);
  const {
    approvals,
    loading: plansLoading,
    error: plansError,
    refetch: refetchPlans,
  } = useSiteApprovals();
  const { persona } = usePersona();
  const { domainOf } = useAssetDomainOf(siteId);

  const items = useMemo<ClosedItem[]>(() => {
    const toItem = (raw: Alarm, source: 'console' | 'anomalies'): ClosedItem => {
      const group = domainOf(raw);
      const a = group === raw.group ? raw : { ...raw, group };
      const itemKind = closedKindOf(a, source);
      return {
        key: `${source}:${a.uid}`,
        kind: itemKind,
        closedAt: ms(a.closedAt),
        raisedAt: ms(a.eventTimestamp),
        haystack: hay(a.asset, a.id, a.alarmId, a.siteLabel ?? siteLabel, a.siteId, a.location),
        node: (
          <AlarmCard
            alarm={a}
            layout={layout}
            closedRecord
            kindLabel={KIND_LABEL[itemKind]}
            onOpen={() =>
              navigate(source === 'anomalies' ? anomalyDetailPath(a) : alarmDetailPath(a))
            }
          />
        ),
      };
    };
    const consoleRows: Alarm[] = alarmsQuery.data?.byTab.closed ?? [];
    const closedAlarms = consoleRows.map(a => toItem(a, 'console'));
    const closedFindings = anomaliesQuery.items
      .filter(a => a.closed)
      .map(a => toItem(a, 'anomalies'));
    // Ops Manager sees every call they made; everyone else only what ran.
    const plans = decidedPlans(approvals, {
      includeRejected: persona === 'manager',
    }).map<ClosedItem>((p: HitlApproval) => ({
      key: `plan:${p.approvalId}`,
      kind: 'recommendation',
      closedAt: ms(p.updatedAt ?? p.createdAt),
      raisedAt: ms(p.createdAt),
      haystack: hay(getStringAttr(p.attributes, 'title'), p.approvalId, siteLabel, p.siteId),
      node: <ClosedPlanCard approval={p} />,
    }));
    return [...closedAlarms, ...closedFindings, ...plans].sort(
      (x, y) => y.closedAt - x.closedAt || y.raisedAt - x.raisedAt
    );
  }, [
    alarmsQuery.data,
    anomaliesQuery.items,
    approvals,
    persona,
    domainOf,
    layout,
    navigate,
    siteLabel,
  ]);

  const counts = useMemo(() => {
    const c: Record<ClosedKind, number> = { alarm: 0, anomaly: 0, routine: 0, recommendation: 0 };
    items.forEach(i => c[i.kind]++);
    return c;
  }, [items]);

  const q = query.trim().toLowerCase();
  const visible = items.filter(
    i => (kind === 'all' || i.kind === kind) && (!q || i.haystack.includes(q))
  );

  // Any source still loading with nothing to show yet: a loader, not a half
  // queue or a false "Nothing closed yet." while the slower query lands.
  const anyLoading = alarmsQuery.isLoading || anomaliesQuery.isLoading || plansLoading;
  if (anyLoading && items.length === 0) return <Loading label="Loading closed items…" />;

  if (alarmsQuery.isError && alarmsQuery.data === undefined && items.length === 0) {
    return (
      <ActionableNotification
        kind="error"
        lowContrast
        title="Couldn't load closed items"
        subtitle={(alarmsQuery.error as Error)?.message ?? 'Request failed'}
        actionButtonLabel="Retry"
        onActionButtonClick={() => alarmsQuery.refetch()}
        hideCloseButton
      />
    );
  }

  return (
    <>
      {alarmsQuery.isError && (
        <StaleDataNotice
          what="closed alarms"
          error={alarmsQuery.error}
          onRetry={() => alarmsQuery.refetch()}
        />
      )}
      {anomaliesQuery.isError && (
        <StaleDataNotice
          what="closed anomalies"
          error={anomaliesQuery.error}
          onRetry={() => anomaliesQuery.refetch()}
        />
      )}
      {plansError && (
        <StaleDataNotice what="decided plans" error={plansError} onRetry={refetchPlans} />
      )}

      <div className="hs-closed-toolbar">
        <Search
          size="md"
          labelText="Search closed items"
          placeholder="Search by title, id or site…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onClear={() => setQuery('')}
        />
      </div>

      <div className="hs-group-pills" role="group" aria-label="Filter by kind">
        <button
          type="button"
          className={`hs-group-pill${kind === 'all' ? ' active' : ''}`}
          aria-pressed={kind === 'all'}
          onClick={() => setKind('all')}
        >
          All <span className="hs-pill-count">{items.length}</span>
        </button>
        {CLOSED_KINDS.map(k => (
          <button
            key={k.key}
            type="button"
            className={`hs-group-pill${kind === k.key ? ' active' : ''}`}
            aria-pressed={kind === k.key}
            onClick={() => setKind(k.key)}
          >
            {k.label} <span className="hs-pill-count">{counts[k.key]}</span>
          </button>
        ))}
        {(alarmsQuery.isFetching || anomaliesQuery.isFetching) && (
          <span className="hs-alarm-fetching">Refreshing…</span>
        )}
      </div>

      {items.length === 0 ? (
        <EmptyState
          big="Nothing closed yet."
          sub="Closed alarms, closed anomalies and decided plans for this site appear here."
        />
      ) : visible.length === 0 ? (
        <EmptyState
          big="No closed items match."
          sub={q ? 'Try another title, id or site.' : 'Nothing of this kind has closed.'}
        />
      ) : (
        <div className={layout === 'tiles' ? 'hs-alarm-grid' : 'hs-alarm-rows'}>
          {visible.map(i => (
            <React.Fragment key={i.key}>{i.node}</React.Fragment>
          ))}
        </div>
      )}
    </>
  );
}

export default ClosedQueue;
